// Client ONLY. A passkey PRF secret never belongs in an API payload or storage.
import { backupBindingBytes, fromBase64url, keyBackupSchema, toBase64url, type BackupBinding, type KeyBackup } from "@link/shared/api/passkeys";
import { entropyToMnemonic, mnemonicToEntropy, mnemonicToSeed } from "../bip39";

async function wrappingKey(prf: Uint8Array, salt: Uint8Array, binding: BackupBinding, version: 1 | 2 = 1): Promise<CryptoKey> {
  if (prf.length !== 32 || salt.length !== 32) throw new Error("Invalid passkey encryption material");
  const source = await crypto.subtle.importKey("raw", prf, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt, info: backupBindingBytes(binding, version) }, source, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

// A stable, public, versioned PRF input allows discoverable (username-less)
// sign-in. Per-credential PRF secrets remain distinct; HKDF adds a random salt.
export async function passkeyPrfInput(rpId: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`link.identity-backup.prf.v1:${rpId}`)));
}

export async function encryptIdentityBackup(seed: Uint8Array, prf: Uint8Array, binding: BackupBinding): Promise<KeyBackup> {
  if (seed.length !== 64) throw new Error("Invalid identity seed");
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await wrappingKey(prf, salt, binding);
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: backupBindingBytes(binding), tagLength: 128 }, key, seed);
  return { version: 1, salt: toBase64url(salt), iv: toBase64url(iv), ciphertext: toBase64url(new Uint8Array(ciphertext)) };
}

export async function decryptIdentityBackup(input: KeyBackup, prf: Uint8Array, binding: BackupBinding): Promise<Uint8Array> {
  if (input.version === 2) return mnemonicToSeed(await decryptRecoveryPhrase(input, prf, binding));
  const backup = keyBackupSchema.parse(input);
  const iv = fromBase64url(backup.iv);
  const ciphertext = fromBase64url(backup.ciphertext);
  if (iv.length !== 12 || ciphertext.length !== 80) throw new Error("Invalid encrypted backup");
  const key = await wrappingKey(prf, fromBase64url(backup.salt), binding);
  return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv, additionalData: backupBindingBytes(binding), tagLength: 128 }, key, ciphertext));
}

export async function encryptRecoveryBackup(phrase: string, prf: Uint8Array, binding: BackupBinding): Promise<KeyBackup> {
  const entropy = await mnemonicToEntropy(phrase);
  try {
    const salt = crypto.getRandomValues(new Uint8Array(32));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await wrappingKey(prf, salt, binding, 2);
    const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: backupBindingBytes(binding, 2), tagLength: 128 }, key, entropy);
    return { version: 2, salt: toBase64url(salt), iv: toBase64url(iv), ciphertext: toBase64url(new Uint8Array(encrypted)) };
  } finally { entropy.fill(0); }
}

export async function decryptRecoveryPhrase(input: KeyBackup, prf: Uint8Array, binding: BackupBinding): Promise<string> {
  const backup = keyBackupSchema.parse(input);
  if (backup.version !== 2) throw new Error("This older backup does not contain your recovery phrase. Use the copy you saved when you created the account.");
  const key = await wrappingKey(prf, fromBase64url(backup.salt), binding, 2);
  const entropy = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromBase64url(backup.iv), additionalData: backupBindingBytes(binding, 2), tagLength: 128 }, key, fromBase64url(backup.ciphertext)));
  try { return await entropyToMnemonic(entropy); }
  finally { entropy.fill(0); }
}
